import { apiSlice } from './apiSlice'

/**
 * Sale Allocation › Edit (Booking) — Access `frmCtrlSalesQrySaleAlc_EditBK_Add`
 * (subform `frmCtrlSalesQrySaleAlc_EditBK`, table `public.sales_allocation_scs`,
 * PK `sales_allocation_scs_id`).
 *
 * This is the toolbar action "Edit (Booking)" on the Sales Allocation tab. The
 * Access main-form opens with NO selected row: the user picks a Booking No in the
 * header combo, which loads every allocation row carrying that booking_no into a
 * grid (Access copies `public_v_sales_allocation_scs` → temp table and requeries
 * the subform). The user edits per-row container/seal/bags/tare fields, then Save
 * runs one UPDATE per row over `public_sales_allocation_scs` (only the 6 editable
 * columns + audit; `WHERE sales_allocation_scs_id = … AND is_status = 0`).
 *
 * NOTE — BACKEND NOT YET BUILT. The Sales Control list reads
 * `public.mv_sales_allocation_scs` (api_erp_prod.py); there is no booking-lookup
 * nor batch-update endpoint for this action. Every endpoint below is a placeholder
 * mirroring the proven `saleAllocationApi` / `saleDeliveryOrderApi` shape so the
 * dialog compiles and wires today; the URLs marked `TODO BE` must be implemented
 * before this dialog is enabled. Queries fall back to empty (no silent fake data).
 *
 * Cache: mutation invalidates `SaleControl` so the Sales Allocation datasheet
 * refetches (Access `Forms!frmCtrlSalesMain!navCtrlSales.Form!subForm.Requery`).
 */

/** One allocation row loaded for a booking_no (subform `RecordSource`). */
export interface SaleAlcEditBookingRow {
  sales_allocation_scs_id: number
  /* ── Read-only context columns (subform display, no "(Edit)" label) ── */
  sale_contract: string | null
  delivery_order: string | null
  client: string | null
  contract_quality: string | null
  m_date: string | null
  from_warehouse: string | null
  to_warehouse: string | null
  /* ── Editable columns (subform "(Edit)" labels; btnSave UPDATE list) ── */
  container_no: string | null
  seal_no: string | null
  number_bags: number | null
  tare_weight: number | null
  tare_carton_pallet: number | null
  tare_cont: number | null
}

export interface SaleAlcEditBookingRowsResponse { items: SaleAlcEditBookingRow[] }

/** Booking No combo option (Access RowSource: SELECT DISTINCT booking_no, do_no
 *  FROM public_v_delivery_order_scs WHERE booking_no is not null …). */
export interface SaleAlcEditBookingLookupItem { value: string; label: string }
export interface SaleAlcEditBookingLookupResponse { items: SaleAlcEditBookingLookupItem[] }

/** One row's editable payload — only the 6 columns Access UPDATEs per row. */
export interface SaleAlcEditBookingPatch {
  sales_allocation_scs_id: number
  container_no?: string | null
  seal_no?: string | null
  number_bags?: number | null
  tare_weight?: number | null
  tare_carton_pallet?: number | null
  tare_cont?: number | null
}

/** Batch UPDATE payload — booking_no scope + one patch per allocation row. */
export interface SaleAlcEditBookingWrite {
  booking_no: string
  rows: SaleAlcEditBookingPatch[]
}

const saleAllocationEditBookingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── frmCtrlSalesQrySaleAlc_EditBK_Add ─────────────────────────────────
    // TODO BE: GET /sale-controls/sale-alc/edit-booking/lookups/booking-nos —
    // SELECT DISTINCT booking_no FROM public_v_delivery_order_scs
    // WHERE booking_no is not null and Len(Trim(booking_no)) > 0.
    saleAlcEditBookingLookupBookingNos: builder.query<SaleAlcEditBookingLookupResponse, string | void>({
      query: (q) => ({
        url: '/sale-controls/sale-alc/edit-booking/lookups/booking-nos',
        params: q ? { q } : {},
      }),
    }),
    // TODO BE: GET /sale-controls/sale-alc/edit-booking/rows — allocation rows for a
    // booking_no (booking_no_AfterUpdate: SELECT * FROM public_v_sales_allocation_scs
    // WHERE booking_no = ? ORDER BY sales_allocation_scs_id DESC).
    getSaleAlcEditBookingRows: builder.query<SaleAlcEditBookingRowsResponse, string>({
      query: (bookingNo) => ({
        url: '/sale-controls/sale-alc/edit-booking/rows',
        params: { bookingNo },
      }),
      keepUnusedDataFor: 0,
    }),
    // TODO BE: PUT /sale-controls/sale-alc/edit-booking — batch UPDATE
    // (btnSave_Click: one UPDATE per row over public_sales_allocation_scs, setting
    // container_no/seal_no/number_bags/tare_weight/tare_carton_pallet/tare_cont +
    // updated_date/updated_by, WHERE sales_allocation_scs_id = ? AND is_status = 0).
    updateSaleAlcEditBooking: builder.mutation<{ updated: number }, SaleAlcEditBookingWrite>({
      query: (body) => ({ url: '/sale-controls/sale-alc/edit-booking', method: 'PUT', body }),
      invalidatesTags: ['SaleControl'],
    }),
  }),
})

export const {
  useSaleAlcEditBookingLookupBookingNosQuery,
  useLazyGetSaleAlcEditBookingRowsQuery,
  useUpdateSaleAlcEditBookingMutation,
} = saleAllocationEditBookingApi

export default saleAllocationEditBookingApi
